// ============================================================================
// PersistentStore - Base Class for Persistent Domain Stores
// ============================================================================

import { ObservableStore } from './observable-store.js';

class PersistentStore extends ObservableStore {
    constructor(config = {}) {
        super();
        this.storageKey = config.storageKey || null;
        this.cacheVersion = config.cacheVersion || 1;
        this.ttl = config.ttl || 24 * 60 * 60 * 1000;
        this.lookupMaps = new Map();
        this.keyedSubscribers = new Map();
        this.data = {};
        this.lastFetch = null;
        this.isInitialized = false;
        this.initPromise = null;
    }

    // Lookup Map Infrastructure
    registerLookupMap(name, keyExtractor, secondaryKeyExtractor = null) {
        this.lookupMaps.set(name, { keyExtractor, secondaryKeyExtractor, map: new Map() });
    }

    getById(mapName, id) {
        return this.lookupMaps.get(mapName)?.map.get(id) ?? null;
    }

    rebuildLookupMap(mapName, items) {
        const lookup = this.lookupMaps.get(mapName);
        if (!lookup) return;

        lookup.map.clear();
        for (const item of items) {
            const primaryKey = lookup.keyExtractor(item);
            if (primaryKey != null) {
                lookup.map.set(primaryKey, item);
            }
            if (lookup.secondaryKeyExtractor) {
                const secondaryKey = lookup.secondaryKeyExtractor(item);
                if (secondaryKey != null && secondaryKey !== primaryKey) {
                    lookup.map.set(secondaryKey, item);
                }
            }
        }
    }

    rebuildAllLookupMaps() {
        // Subclasses should override
    }

    // Persistence
    loadFromStorage() {
        if (!this.storageKey) return null;

        try {
            const cached = localStorage.getItem(this.storageKey);
            if (!cached) return null;

            const parsed = JSON.parse(cached);
            if (parsed.version !== this.cacheVersion) {
                localStorage.removeItem(this.storageKey);
                return null;
            }
            if (!parsed.data || !parsed.lastFetch) return null;

            return parsed;
        } catch (error) {
            console.error(`[${this.constructor.name}] Cache load failed:`, error.message);
            return null;
        }
    }

    saveToStorage() {
        if (!this.storageKey) return;

        try {
            localStorage.setItem(this.storageKey, JSON.stringify({
                version: this.cacheVersion,
                data: this.data,
                lastFetch: this.lastFetch
            }));
        } catch (error) {
            console.error(`[${this.constructor.name}] Cache save failed:`, error.message);
        }
    }

    shouldRefresh() {
        return !this.lastFetch || Date.now() - this.lastFetch > this.ttl;
    }

    clearStorage() {
        if (this.storageKey) {
            localStorage.removeItem(this.storageKey);
        }
        this.data = {};
        this.lastFetch = null;
        this.lookupMaps.forEach(lookup => lookup.map.clear());
    }

    // Subscriber Pattern (keyed + event-based)
    subscribe(keyOrEvent, callback) {
        if (keyOrEvent.includes(':')) {
            return super.subscribe(keyOrEvent, callback);
        }
        this.keyedSubscribers.set(keyOrEvent, callback);
        return () => this.keyedSubscribers.delete(keyOrEvent);
    }

    notifyKeyedSubscribers(changedType = null) {
        for (const callback of this.keyedSubscribers.values()) {
            try {
                callback(this.data, changedType);
            } catch (error) {
                console.error(`[${this.constructor.name}] Subscriber error:`, error.message);
            }
        }
    }

    notifySubscribers(changedType = null) {
        this.notifyKeyedSubscribers(changedType);
        this.emit('data:changed', { type: changedType, data: this.data });
    }

    // Initialization
    async init() {
        if (this.initPromise) return this.initPromise;
        this.initPromise = this._doInit();
        return this.initPromise;
    }

    async _doInit() {
        this.isInitialized = true;
    }

    // Cleanup
    destroy() {
        super.destroy();
        this.keyedSubscribers.clear();
        this.lookupMaps.forEach(lookup => lookup.map.clear());
        this.lookupMaps.clear();
        this.data = {};
        this.lastFetch = null;
        this.isInitialized = false;
        this.initPromise = null;
    }
}

export { PersistentStore };
